/** Ball sequence labeler (port of sequence_labeler.py's inline page,
 *  same address /labeling/sequence/).
 *
 * Segment list → four panels per batch. Unzoomed click near the ball
 * zooms in centered there (stay on the panel to refine); zoomed click
 * marks and seeds every later unmarked panel with the same position.
 * Keys: n = not visible, z = toggle zoom, 1-4 = jump panel, Enter =
 * save the four and load the next. Same hash routing (#slug), same
 * submit payload {seg, labels:[{frame,task_id,x,y,novis,w,h}]}, same
 * "segment complete 🎉".
 *
 * Imperative DOM (refs + direct style writes) on purpose: the zoom
 * transform and mark overlay are the Jinja page's math verbatim, and
 * a React re-render per mouse move would fight the image transforms.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../../lib/api'
import './labeling.css'

const Z = 2.2

type Frame = { frame: number; task_id: number; image: string }
type Seg = { slug: string; game: string; start_frame: number; n_frames: number;
             done: number; locked_by: string | null }
type PanelState = { x: number | null; y: number | null; novis: boolean; mode: 'fit' | 'zoom' }
type BatchPayload = { error?: string; frames: Frame[]; seed?: { x: number; y: number } | null;
                      done: number; n_frames: number }

export default function SequenceLabeler() {
  const [seg, setSeg] = useState<string | null>(
    window.location.hash.replace('#', '') || null)
  const [segments, setSegments] = useState<Seg[] | null>(null)
  const [frames, setFrames] = useState<Frame[]>([])
  const [status, setStatus] = useState('')
  const [active, setActive] = useState(0)
  const [batchMeta, setBatchMeta] = useState<{ done: number; n_frames: number } | null>(null)

  const state = useRef<PanelState[]>([])
  const lastPos = useRef<{ x: number; y: number } | null>(null)
  const panels = useRef<(HTMLDivElement | null)[]>([])
  const imgs = useRef<(HTMLImageElement | null)[]>([])
  const activeRef = useRef(0)
  const framesRef = useRef<Frame[]>([])

  // Same hash routing the Jinja page used, so segment deep-links and
  // the segments button keep working.
  useEffect(() => {
    const onHash = () => setSeg(window.location.hash.replace('#', '') || null)
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const view = (i: number) => {
    const d = panels.current[i]; const im = imgs.current[i]
    if (!d || !im) return
    const W = d.clientWidth; const H = d.clientHeight
    const iw = im.naturalWidth; const ih = im.naturalHeight
    const st = state.current[i]!
    let s: number; let cx: number; let cy: number
    if (st.mode === 'zoom' && st.x != null) { s = Z * W / iw; cx = st.x; cy = st.y! }
    else { s = Math.min(W / iw, H / ih); cx = iw / 2; cy = ih / 2 }
    im.style.transform = `scale(${s})`
    im.style.left = `${W / 2 - cx * s}px`
    im.style.top = `${H / 2 - cy * s}px`
    im.dataset.s = String(s)
  }

  const paint = (i: number) => {
    const d = panels.current[i]; const im = imgs.current[i]
    if (!d || !im) return
    d.querySelectorAll('.mark').forEach((m) => m.remove())
    d.classList.remove('done', 'novis')
    const st = state.current[i]!
    if (st.novis) { d.classList.add('novis'); return }
    if (st.x == null) return
    const s = parseFloat(im.dataset.s || '1')
    const m = document.createElement('div')
    m.className = 'mark'
    m.style.left = `${parseFloat(im.style.left) + st.x * s}px`
    m.style.top = `${parseFloat(im.style.top) + st.y! * s}px`
    d.appendChild(m)
    d.classList.add('done')
  }

  const activate = (i: number) => { activeRef.current = i; setActive(i) }

  const render = useCallback((j: BatchPayload) => {
    if (j.error) { setStatus(j.error); return }
    if (!j.frames.length) {
      setStatus('segment complete 🎉'); setFrames([]); framesRef.current = []
      return
    }
    lastPos.current = j.seed || lastPos.current
    state.current = j.frames.map(() => ({
      x: lastPos.current?.x ?? null, y: lastPos.current?.y ?? null,
      novis: false, mode: lastPos.current ? 'zoom' : 'fit',
    }))
    framesRef.current = j.frames
    setFrames(j.frames)
    setBatchMeta({ done: j.done, n_frames: j.n_frames })
    setStatus('')
    activate(0)
  }, [])

  const loadBatch = useCallback((slug: string) => {
    setStatus('loading...')
    api<BatchPayload>(`/labeling/sequence/batch?seg=${encodeURIComponent(slug)}`)
      .then(render)
      .catch((e: Error) => setStatus(e.message))
  }, [render])

  useEffect(() => {
    if (seg) { loadBatch(seg) } else {
      api<{ segments: Seg[] }>('/labeling/sequence/segments')
        .then((j) => setSegments(j.segments))
        .catch((e: Error) => setStatus(e.message))
    }
  }, [seg, loadBatch])

  const mark = (i: number, e: React.MouseEvent) => {
    const d = panels.current[i]; const im = imgs.current[i]
    if (!d || !im) return
    const r = d.getBoundingClientRect()
    const s = parseFloat(im.dataset.s || '1')
    const x = (e.clientX - r.left - parseFloat(im.style.left)) / s
    const y = (e.clientY - r.top - parseFloat(im.style.top)) / s
    if (state.current[i]!.mode === 'fit') {
      // rough click: zoom in centered there, stay on this panel to refine
      state.current[i] = { x, y, novis: false, mode: 'zoom' }
      view(i); paint(i); activate(i)
      return
    }
    state.current[i] = { x, y, novis: false, mode: 'zoom' }
    if (i === framesRef.current.length - 1) lastPos.current = { x, y }
    // seed later unmarked panels with this position, zoomed
    for (let k = i + 1; k < framesRef.current.length; k++) {
      if (!state.current[k]!.novis && state.current[k]!.x == null) {
        state.current[k] = { x, y, novis: false, mode: 'zoom' }
        view(k); paint(k)
      }
    }
    view(i); paint(i)
    activate(Math.min(i + 1, framesRef.current.length - 1))
  }

  const submit = useCallback(async () => {
    const labels = framesRef.current.map((f, i) => {
      const im = imgs.current[i]
      const st = state.current[i]!
      return { frame: f.frame, task_id: f.task_id, x: st.x, y: st.y,
               novis: st.novis, w: im?.naturalWidth ?? 0, h: im?.naturalHeight ?? 0 }
    })
    if (labels.some((l) => !l.novis && l.x == null)) {
      setStatus('mark every panel (or n) first'); return
    }
    setStatus('saving...')
    try {
      const j = await api<BatchPayload>('/labeling/sequence/submit', {
        method: 'POST', body: JSON.stringify({ seg, labels }),
      })
      render(j)
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'save failed')
    }
  }, [seg, render])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!seg || !framesRef.current.length) return
      const a = activeRef.current
      if (e.key === 'n') {
        state.current[a] = { x: null, y: null, novis: !state.current[a]!.novis, mode: 'fit' }
        view(a); paint(a)
        activate(Math.min(a + 1, framesRef.current.length - 1))
      }
      if (e.key === 'z') {
        state.current[a]!.mode = state.current[a]!.mode === 'zoom' ? 'fit' : 'zoom'
        view(a); paint(a)
      }
      if (e.key >= '1' && e.key <= '4') activate(+e.key - 1)
      if (e.key === 'Enter') { e.preventDefault(); submit() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [seg, submit])

  const stats = status || (seg && frames.length && batchMeta
    ? `${seg} · frame ${frames[0]!.frame} · ${batchMeta.done}/${batchMeta.n_frames} labeled`
    : '')

  return (
    <div className="lab-sequence">
      <div id="bar">
        {' '}<b>sequence labeler</b>{' '}
        <span className="k">
          unzoomed: click near ball zooms in &nbsp; zoomed: click = mark &nbsp;{' '}
          <b>n</b> = not visible &nbsp; <b>z</b> = zoom out &nbsp;{' '}
          <b>Enter</b> save 4 &amp; next &nbsp; <b>1-4</b> = jump panel
        </span>{' '}
        <button className="sec" onClick={() => { window.location.hash = '' }}>segments</button>{' '}
        <span id="stats">{stats}</span>
      </div>

      {!seg && (
        <div id="segs">
          {segments?.map((s) => {
            const pct = Math.round(100 * s.done / s.n_frames)
            return (
              <a key={s.slug} href={`#${s.slug}`}>
                <b>{s.game}</b> · frames {s.start_frame}-{s.start_frame + s.n_frames - 1}
                <span className="segdone">
                  {s.done}/{s.n_frames} ({pct}%){s.locked_by ? ` · in use: ${s.locked_by}` : ''}
                </span>
              </a>
            )
          })}
        </div>
      )}

      {seg && (
        <div id="panels">
          {frames.map((f, i) => (
            <div key={f.task_id} className={`panel ${i === active ? 'active' : ''}`}
                 ref={(el) => { panels.current[i] = el }}
                 onClick={(e) => mark(i, e)}>
              <img src={f.image} alt=""
                   ref={(el) => { imgs.current[i] = el }}
                   onLoad={() => {
                     if (lastPos.current && state.current[i]!.x == null
                         && !state.current[i]!.novis) {
                       state.current[i]!.x = lastPos.current.x
                       state.current[i]!.y = lastPos.current.y
                     }
                     view(i); paint(i)
                   }} />
              <span className="fl">{f.frame}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
